import { useEffect, useState } from 'react';
import { Button, Card } from '@/components/ui';
import {
  disablePush,
  enablePush,
  fetchPublicKey,
  isSubscribed,
  pushSupported,
  sendTestPush,
} from '@/lib/push';

/**
 * 폰 알림(Web Push) 설정. 내 정보 화면에 붙는다. (2026-08-22)
 *
 * 🔴 아이폰은 홈 화면에 추가된 앱에서 열어야 켤 수 있다 (iOS 16.4+)
 *   사파리 탭에서는 pushManager 가 없어 안내 문구가 뜬다.
 *
 * 서버에 VAPID 키가 없으면(발급 전) 카드 자체를 숨긴다 -
 * 켤 수 없는 스위치를 보여주는 것보다 낫다.
 */
export function PushSettings() {
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const supported = pushSupported();

  useEffect(() => {
    if (!supported) {
      return;
    }
    void fetchPublicKey().then((key) => {
      setPublicKey(key);
      if (key) {
        void isSubscribed().then(setEnabled);
      }
    });
  }, [supported]);

  // 키 발급 전이면 카드를 숨긴다. 지원 안 되는 브라우저는 안내를 위해 보여준다
  if (supported && publicKey === null) {
    return null;
  }

  const toggle = async () => {
    setPending(true);
    setMessage(null);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        setMessage('알림을 껐습니다.');
      } else {
        await enablePush(publicKey as string);
        setEnabled(true);
        setMessage('알림을 켰습니다. 테스트 발송으로 확인해 보세요.');
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '알림 설정에 실패했습니다.');
    } finally {
      setPending(false);
    }
  };

  /*
    테스트 발송. (2026-09-03 결과 문구 개정)

    🔴 "몇 대로 보냈다" 만으로는 확인이 안 된다
      사용자가 PC 에서 눌렀는데 폰에만 오는 일이 있었다. 그때 화면이
      "1대로 보냈습니다" 라고만 하면 그 1대가 폰인지 PC 인지 알 수 없다.
      켜둔 기기 수와 성공 수를 함께 말하고, 안 온 기기가 있으면
      어디를 봐야 하는지까지 적는다.
  */
  const test = async () => {
    setPending(true);
    try {
      const result = await sendTestPush();

      if (result.devices === 0) {
        setMessage(
          result.slack
            ? 'Slack 으로만 보냈습니다. 켜둔 기기가 없습니다 — 이 기기의 알림을 먼저 켜 주세요.'
            : // ✏️ 9/28 I-78 2차 — "Slack 을 켜 주세요" 는 관리자가 아니면 할 수 없는 안내였다(I-82 · 관리자만 Slack)
              '보낼 곳이 없습니다. 이 기기의 알림을 먼저 켜 주세요.',
        );
        return;
      }

      const parts = [`켜둔 기기 ${result.devices}대 중 ${result.sent}대 도착`];
      // "꺼둠" 은 본인이 끈 것으로 읽힌다 — 관리자가 아니면 켤 수 없어 꺼져 있는 것이다 (9/28)
      parts.push(result.slack ? 'Slack 발송함' : 'Slack 은 보내지 않음');

      const tail =
        result.sent < result.devices
          ? ' 안 온 기기는 브라우저·OS 의 알림 권한이 막혔을 수 있습니다.'
          : ' 알림이 안 보이면 브라우저와 OS(윈도우 알림 센터)의 권한을 확인하세요.';

      setMessage(parts.join(' · ') + '.' + tail);
    } catch {
      setMessage('테스트 발송에 실패했습니다.');
    } finally {
      setPending(false);
    }
  };

  return (
    <Card title="폰 알림">
      {supported ? (
        <>
          <p className="text-xs text-fg-muted">
            이 기기로 알림을 받습니다. 가격 알림·혜택 일정 등 개인 알림이 여기로 옵니다.
            {' '}
            {/*
              🔴 자가 복구를 화면이 말해준다 (2026-09-04)
                업데이트 때 잠깐 꺼졌다 다시 켜지는 것이 정상 동작이라고 알려야,
                그 순간을 본 사용자가 "또 꺼졌네" 로 읽지 않는다.
            */}
            <span className="text-fg-subtle">
              한 번 켜두면 앱을 업데이트해도 유지됩니다 — 갱신 중 잠깐 끊겼다가
              다시 열 때 스스로 복구합니다.
            </span>
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant={enabled ? 'secondary' : 'primary'} disabled={pending} onClick={() => void toggle()}>
              {enabled ? '알림 끄기' : '알림 켜기'}
            </Button>
            {enabled && (
              <Button variant="ghost" disabled={pending} onClick={() => void test()}>
                테스트 발송
              </Button>
            )}
          </div>
        </>
      ) : (
        <p className="text-xs text-fg-muted">
          이 브라우저에서는 알림을 켤 수 없습니다. 아이폰은 사파리에서{' '}
          <strong>공유 → 홈 화면에 추가</strong>로 설치한 앱에서 열면 켤 수 있습니다.
        </p>
      )}

      {message && <p className="mt-2 text-xs text-fg-muted">{message}</p>}
    </Card>
  );
}
